import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { actions } from './globalReducer';

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'account_enabled', label: 'Account enabled' },
  { value: 'account_disabled', label: 'Account disabled' },
  { value: 'account_onboarded', label: 'Account onboarded' },
  { value: 'user_granted_ai_guardian_admin', label: 'User granted admin' },
  { value: 'user_revoked_ai_guardian_admin', label: 'User revoked admin' },
  { value: 'member_role_changed', label: 'Member role changed' },
  { value: 'support_granted_admin', label: 'User granted admin (support)' },
  { value: 'support_revoked_admin', label: 'User revoked admin (support)' },
  { value: 'support_onboarded_account', label: 'Account onboarded (support)' },
  { value: 'support_edited_backend_definition', label: 'Backend definition edited (support)' },
  { value: 'support_edited_account_backend_override', label: 'Backend override edited (support)' },
];

const ACTION_LABEL = Object.fromEntries(
  ACTION_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
);

const ACTION_BDS_TYPE = {
  account_enabled: 'staticSuccess',
  account_onboarded: 'staticEmphasis',
  account_disabled: 'staticNeutral',
  user_granted_ai_guardian_admin: 'staticEmphasis',
  user_revoked_ai_guardian_admin: 'staticNeutral',
  member_role_changed: 'staticNeutral',
  support_granted_admin: 'staticEmphasis',
  support_revoked_admin: 'staticNeutral',
  support_onboarded_account: 'staticEmphasis',
  support_edited_backend_definition: 'staticNeutral',
  support_edited_account_backend_override: 'staticNeutral',
};

// The backend uses the same `support_edited_*` action for create / update /
// delete on backend definitions and overrides — the details.deleted /
// details.created flags disambiguate. Show that in the chip so a delete
// stands out from a plain edit at a glance.
const chipDisplayForEntry = (entry) => {
  const baseLabel = ACTION_LABEL[entry.action] || entry.action;
  const baseType = ACTION_BDS_TYPE[entry.action] || 'staticNeutral';
  const isBackendMutation =
    entry.action === 'support_edited_backend_definition' ||
    entry.action === 'support_edited_account_backend_override';
  if (isBackendMutation && entry.details?.deleted) {
    return {
      label: entry.action === 'support_edited_backend_definition'
        ? 'Backend definition deleted (support)'
        : 'Backend override removed (support)',
      bdsType: 'errorNeutral',
    };
  }
  if (isBackendMutation && entry.details?.created) {
    return {
      label: entry.action === 'support_edited_backend_definition'
        ? 'Backend definition created (support)'
        : 'Backend override created (support)',
      bdsType: 'staticEmphasis',
    };
  }
  return { label: baseLabel, bdsType: baseType };
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const summarizeBackendOverrideDetails = (details) => {
  if (!details) return '';
  if (details.deleted) {
    return `Removed override (was ${details.url || '—'}, timeout ${details.timeout ?? '—'}s)`;
  }
  if (details.created) {
    return `Created — ${details.url || '—'} (timeout ${details.timeout ?? '—'}s)`;
  }
  // update: show a compact "field: value" list
  const parts = [];
  if ('url' in details) parts.push(`url=${details.url}`);
  if ('timeout' in details) parts.push(`timeout=${details.timeout}s`);
  if ('enabled' in details) parts.push(`enabled=${details.enabled}`);
  if ('has_api_token' in details) parts.push(`has_api_token=${details.has_api_token}`);
  return parts.length ? parts.join(' · ') : JSON.stringify(details);
};

const formatDetails = (entry) => {
  if (entry.action === 'member_role_changed' && entry.details) {
    const { previous_role, new_role, account_name } = entry.details;
    return `${previous_role} -> ${new_role} in ${account_name || 'unknown'}`;
  }
  if (entry.action === 'account_enabled' && entry.details?.via === 'onboard') {
    return 'via onboard';
  }
  if (
    entry.action === 'support_edited_account_backend_override' ||
    entry.action === 'support_edited_backend_definition'
  ) {
    return summarizeBackendOverrideDetails(entry.details);
  }
  if (!entry.details || Object.keys(entry.details).length === 0) return '';
  return JSON.stringify(entry.details);
};

const AuditLogContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const { items, isLoading, error } = useSelector(
    (state) => state.global.audit
  );

  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');

  useEffect(() => {
    const filters = {};
    if (actionFilter) filters.action = actionFilter;
    if (actorFilter) filters.actor = actorFilter;
    dispatch(actions.fetchAudit(filters));
  }, [dispatch, actionFilter, actorFilter]);

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        sx={{
          gap: tokens.aliasesSpaceComponentDense,
          marginBottom: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <Typography variant="h2">Audit log</Typography>
        <Typography variant="captionSubtle">
          Append-only record of admin actions. Routine reads are not logged.
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: tokens.aliasesSpaceComponentDefault,
          marginBottom: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <TextField
          select
          label="Action"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          bdsSize="small"
          sx={{ minWidth: 220 }}
        >
          {ACTION_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Actor email"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          bdsSize="small"
          placeholder="substring match"
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {error && (
        <Typography
          variant="body1"
          sx={{
            color: tokens.aliasesColorTextError,
            marginBottom: tokens.aliasesSpaceContainerDefault,
          }}
        >
          {error}
        </Typography>
      )}

      {isLoading && items.length === 0 ? (
        <Typography variant="body1">Loading…</Typography>
      ) : items.length === 0 ? (
        <Typography variant="captionSubtle">No audit entries match.</Typography>
      ) : (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
            '& th, & td': {
              padding: tokens.aliasesSpaceContainerDefault,
              borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
              textAlign: 'left',
              verticalAlign: 'top',
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 180 }}><Typography variant="body1Bold">When</Typography></th>
              <th><Typography variant="body1Bold">Actor</Typography></th>
              <th><Typography variant="body1Bold">Action</Typography></th>
              <th><Typography variant="body1Bold">Target</Typography></th>
              <th><Typography variant="body1Bold">Details</Typography></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td><Typography variant="captionSubtle">{fmtDate(e.timestamp)}</Typography></td>
                <td><Typography variant="body1">{e.actor_username}</Typography></td>
                <td>
                  <Chip
                    label={chipDisplayForEntry(e).label}
                    bdsType={chipDisplayForEntry(e).bdsType}
                    bdsSize="medium"
                  />
                </td>
                <td>
                  <Typography variant="body1">{e.target_label || '—'}</Typography>
                  {e.target_id && (
                    <Typography
                      variant="captionSubtle"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {e.target_type} {e.target_id}
                    </Typography>
                  )}
                </td>
                <td><Typography variant="captionSubtle">{formatDetails(e)}</Typography></td>
              </tr>
            ))}
          </tbody>
        </Box>
      )}
    </Box>
  );
};

export default AuditLogContainer;
